import { apiClient } from './api'

export interface ExecutiveOverview {
  summary: {
    total_projects: number
    total_portfolios: number
    total_tasks: number
    active_projects: number
    total_budget: number
    total_actual_cost: number
    budget_variance: number
    budget_utilization_percent: number
    average_progress: number
  }
  project_status_breakdown: Record<string, number>
  portfolio_status_breakdown: Record<string, number>
  task_status_breakdown: Record<string, number>
}

export interface PortfolioSummary {
  id: number
  name: string
  status: string
  project_count: number
  program_count?: number
  total_budget: number
  total_actual_cost: number
  budget_variance: number
  average_progress: number
  start_date: string | null
  end_date: string | null
}

export interface ProjectTimelineItem {
  id: number
  name: string
  status: string
  start_date: string | null
  end_date: string | null
  progress: number
  budget: number
  actual_cost: number
  portfolio_id: number | null
}

export interface MonthlyTrend {
  month: string
  projects_created: number
  total_budget: number
  total_actual_cost: number
}

export interface RiskIndicator {
  type: string
  severity: 'high' | 'medium' | 'low'
  project_id?: number
  project_name?: string
  message: string
  count?: number
  budget?: number
  actual_cost?: number
  due_date?: string
  progress?: number
  created_at?: string
}

export interface InsightNavTarget {
  // Known kinds: 'project' | 'task' | 'portfolio' | 'program' | 'tasks' |
  // 'projects' | 'risks'. Unknown kinds are ignored by the UI.
  kind: string
  id?: number | null
  tab?: string | null
}

export interface ExecutiveInsightItem {
  summary: string
  reasoning?: string
  urgency?: 'critical' | 'high' | 'medium' | 'low'
  target_role?: string
  nav?: InsightNavTarget | null
}

export interface ExecutiveInsights {
  working: ExecutiveInsightItem[]
  not_working: ExecutiveInsightItem[]
  needs_decision: ExecutiveInsightItem[]
  emerging_concerns: ExecutiveInsightItem[]
  ai_powered?: boolean
  /** When these were generated; they're cached per workspace for a few hours. */
  generated_at?: string
  cached?: boolean
}

export interface PendingApproval {
  id: string
  type: 'stage_gate' | 'change_request' | 'decision' | 'escalation'
  project_id: number
  project_name: string
  title: string
  days_waiting: number
  priority: 'critical' | 'high' | 'medium'
  created_at: string
  amount?: number
}

export interface PendingApprovalsList {
  pending_approvals: PendingApproval[]
}

// `flat` is returned when no period-over-period delta can be computed
// (e.g. no snapshot history exists yet). The UI hides the change widget
// for flat trends — see HealthMetricsGrid.
export type HealthTrend = 'up' | 'down' | 'flat'

export interface HealthMetrics {
  portfolio_health_score: number
  portfolio_health_change: number
  portfolio_health_trend: HealthTrend
  delivery_confidence: number
  delivery_confidence_change: number
  delivery_confidence_trend: HealthTrend
  cost_at_risk: number
  cost_at_risk_change: number
  cost_at_risk_trend: HealthTrend
  schedule_stability: number
  schedule_stability_change: number
  schedule_stability_trend: HealthTrend
  decision_latency_days: number
  decision_latency_change: number
  decision_latency_trend: HealthTrend
  resource_stress: number
  resource_stress_change: number
  resource_stress_trend: HealthTrend
  alignment_score: number
  alignment_score_change: number
  alignment_score_trend: HealthTrend
}

export const dashboardService = {
  /**
   * Get executive overview metrics
   */
  async getExecutiveOverview(): Promise<ExecutiveOverview> {
    const response = await apiClient.get<ExecutiveOverview>('/api/v1/dashboard/executive/overview')
    return response.data
  },

  /**
   * Get portfolio summary
   */
  async getPortfolioSummary(): Promise<{ portfolios: PortfolioSummary[] }> {
    const response = await apiClient.get<{ portfolios: PortfolioSummary[] }>('/api/v1/dashboard/executive/portfolios')
    return response.data
  },

  /**
   * Get projects timeline
   */
  async getProjectsTimeline(daysAhead: number = 90): Promise<{
    timeline_start: string
    timeline_end: string
    projects: ProjectTimelineItem[]
  }> {
    const response = await apiClient.get('/api/v1/dashboard/executive/projects/timeline', {
      params: { days_ahead: daysAhead },
    })
    return response.data
  },

  /**
   * Get budget trends
   */
  async getBudgetTrends(months: number = 6): Promise<{
    period_months: number
    monthly_trends: MonthlyTrend[]
  }> {
    const response = await apiClient.get('/api/v1/dashboard/executive/budget/trends', {
      params: { months },
    })
    return response.data
  },

  /**
   * Get risk indicators
   */
  async getRiskIndicators(): Promise<{
    total_risks: number
    high_severity: number
    medium_severity: number
    at_risk_projects: number
    risks: RiskIndicator[]
  }> {
    const response = await apiClient.get('/api/v1/dashboard/executive/risks')
    return response.data
  },

  /**
   * Get AI-generated insights
   */
  async getInsights(): Promise<ExecutiveInsights> {
    const response = await apiClient.get<ExecutiveInsights>('/api/v1/dashboard/executive/insights')
    return response.data
  },

  /**
   * Get pending approvals
   */
  async getPendingApprovals(): Promise<PendingApprovalsList> {
    const response = await apiClient.get<PendingApprovalsList>('/api/v1/dashboard/executive/approvals')
    return response.data
  },

  /**
   * Approve a pending approval request
   */
  async approveRequest(approvalId: string): Promise<{ status: string; message: string }> {
    const response = await apiClient.post(`/api/v1/dashboard/executive/approvals/${approvalId}/approve`)
    return response.data
  },

  /**
   * Reject a pending approval request
   */
  async rejectRequest(approvalId: string): Promise<{ status: string; message: string }> {
    const response = await apiClient.post(`/api/v1/dashboard/executive/approvals/${approvalId}/reject`)
    return response.data
  },

  /**
   * Mark an approval request as reviewed
   */
  async reviewRequest(approvalId: string): Promise<{ status: string; message: string }> {
    const response = await apiClient.post(`/api/v1/dashboard/executive/approvals/${approvalId}/review`)
    return response.data
  },

  /**
   * Get health metrics
   */
  async getHealthMetrics(): Promise<HealthMetrics> {
    const response = await apiClient.get<HealthMetrics>('/api/v1/dashboard/executive/health-metrics')
    return response.data
  },

  /** Daily health scores, oldest first (one point per day the dashboard was opened). */
  async getHealthHistory(days = 90): Promise<HealthHistory> {
    const response = await apiClient.get<HealthHistory>('/api/v1/dashboard/executive/health-history', { params: { days } })
    return response.data
  },
}

export interface HealthHistoryPoint {
  date: string
  portfolio_health: number
  schedule_stability: number
  delivery_confidence: number
  resource_stress: number
  cost_at_risk: number
}

export interface HealthHistory {
  days: number
  points: HealthHistoryPoint[]
}
